// Block / remove / restore a person's access to the app.
// All data stays in Firestore; access is controlled by `users.accessStatus`
// ('blocked' | 'removed' | null) plus the Firebase Auth account (via the setUserAccess Cloud Function).
import { db } from '../firebase'
import { collection, doc, getDocs, query, serverTimestamp, where, writeBatch } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'

export const ACCESS_BLOCKED = 'blocked'
export const ACCESS_REMOVED = 'removed'

export function hasRevokedAccess(userData) {
  return userData?.accessStatus === ACCESS_BLOCKED || userData?.accessStatus === ACCESS_REMOVED
}

// Older records may store the email with different casing, so match both forms.
async function findByEmail(collectionName, email) {
  const variants = [...new Set([email, email?.trim().toLowerCase()].filter(Boolean))]
  const snaps = await Promise.all(
    variants.map(v => getDocs(query(collection(db, collectionName), where('email', '==', v))))
  )
  const byId = new Map()
  snaps.forEach(s => s.docs.forEach(d => byId.set(d.id, d)))
  return [...byId.values()]
}

/**
 * @param {{ docId: string, email: string, uid?: string }} user  row from the users collection
 * @param {'block'|'unblock'|'remove'|'restore'} action
 * @param {string} adminEmail  who made the change (audit)
 * @returns {Promise<{ authUpdated: boolean, authError?: Error }>}
 */
export async function changeUserAccess(user, action, adminEmail) {
  const restoring = action === 'unblock' || action === 'restore'
  const now = serverTimestamp()
  const batch = writeBatch(db)

  batch.update(doc(db, 'users', user.docId), {
    accessStatus: restoring ? null : (action === 'block' ? ACCESS_BLOCKED : ACCESS_REMOVED),
    accessChangedAt: now,
    accessChangedBy: adminEmail || ''
  })

  // Applications are hidden from the applicant on remove, shown again on restore
  if (action === 'remove' || action === 'restore') {
    for (const d of await findByEmail('applications', user.email)) {
      batch.update(d.ref, action === 'remove' ? { removed: true, removedAt: now } : { removed: false })
    }
  }

  // Employees are Terminated while blocked/removed; their previous status comes back on restore
  for (const d of await findByEmail('employees', user.email)) {
    const data = d.data()
    if (restoring) {
      if (data.statusBeforeAccessChange) {
        batch.update(d.ref, { status: data.statusBeforeAccessChange, statusBeforeAccessChange: null })
      }
    } else if (!data.statusBeforeAccessChange && data.status !== 'Terminated') {
      batch.update(d.ref, { status: 'Terminated', statusBeforeAccessChange: data.status || 'Active' })
    }
  }

  await batch.commit()

  // Invited people who never created a login have no Auth account to change
  if (!user.uid) return { authUpdated: false }
  try {
    await httpsCallable(getFunctions(), 'setUserAccess')({ uid: user.uid, action })
    return { authUpdated: true }
  } catch (authError) {
    console.error('setUserAccess failed:', authError)
    return { authUpdated: false, authError }
  }
}
